import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const mediaUrl = (path) => (path ? `${BACKEND_URL}${path}` : null);

const http = axios.create({ baseURL: API });
http.interceptors.request.use((config) => {
  const t = localStorage.getItem("bs_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  let d = localStorage.getItem("bs_device");
  if (!d) {
    d = (window.crypto && crypto.randomUUID && crypto.randomUUID()) || String(Date.now() + Math.random());
    localStorage.setItem("bs_device", d);
  }
  config.headers["X-Device-Id"] = d;
  const sb = localStorage.getItem("bs_support_bid");
  if (sb) config.headers["X-Support-Business"] = sb;
  return config;
});

// Backend is the single source of truth. Wipe all client-side customer state so
// a deleted/nonexistent customer can never keep rendering an old card. Keeps
// bs_device (rate-limiting) and bs_slug (which business to return to).
export async function clearCustomerClientState() {
  const slug = localStorage.getItem("bs_slug");
  const device = localStorage.getItem("bs_device");
  try { localStorage.removeItem("bs_token"); } catch (e) {}
  try { sessionStorage.clear(); } catch (e) {}
  try {
    if (window.caches && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (e) {}
  try {
    if (window.indexedDB && indexedDB.databases) {
      const dbs = await indexedDB.databases();
      await Promise.all((dbs || []).map((d) => d && d.name && indexedDB.deleteDatabase(d.name)));
    }
  } catch (e) {}
  try {
    if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) {}
  if (slug) localStorage.setItem("bs_slug", slug);
  if (device) localStorage.setItem("bs_device", device);
}

http.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAuthAttempt = url.includes("/auth/login") || url.includes("/auth/register");
    const isMe = url.includes("/auth/me");
    const isCustomer = url.includes("/my/state");
    const path = window.location.pathname;
    const invalidCustomer = (status === 401 || status === 403 || status === 404) && isCustomer;
    if (invalidCustomer) {
      clearCustomerClientState();
      if (!isMe) {
        const slug = localStorage.getItem("bs_slug");
        if (slug && !path.startsWith("/join/")) window.location.replace(`/join/${slug}`);
        else if (!slug && path !== "/login") window.location.replace("/login");
      }
    } else if (status === 401 && !isAuthAttempt) {
      try { localStorage.removeItem("bs_token"); } catch (e) {}
      if (!isMe && path !== "/login" && !path.startsWith("/join/") && path !== "/register") {
        window.location.replace("/login");
      }
    }
    return Promise.reject(error);
  }
);
export { http };

// ---- Business-scoped customer tokens ----
// Each business remembers its own customer card under bs_ctok:<businessId>.
// bs_token is the ACTIVE token axios attaches; we swap it when switching business.
const CTOK_PREFIX = "bs_ctok:";
export const getCustomerToken = (bid) => {
  try { return bid ? localStorage.getItem(CTOK_PREFIX + bid) : null; } catch (e) { return null; }
};
export const setCustomerToken = (bid, token) => {
  try { if (bid && token) localStorage.setItem(CTOK_PREFIX + bid, token); } catch (e) { /* noop */ }
};
export const removeCustomerToken = (bid) => {
  try { if (bid) localStorage.removeItem(CTOK_PREFIX + bid); } catch (e) { /* noop */ }
};
export const setActiveToken = (token) => {
  try { if (token) localStorage.setItem("bs_token", token); else localStorage.removeItem("bs_token"); } catch (e) { /* noop */ }
};

// ---- Auth (admin/owner/staff) ----
export const registerApi = (name, email, password) =>
  http.post("/auth/register", { name, email, password }).then((r) => r.data);
export const loginApi = (email, password) =>
  http.post("/auth/login", { email, password }).then((r) => r.data);
export const meApi = () => http.get("/auth/me", { params: { _: Date.now() } }).then((r) => r.data);
export const getConfig = () => http.get("/config").then((r) => r.data);
export const logoutApi = () => http.post("/auth/logout").then((r) => r.data);
export const changePasswordApi = (current_password, new_password) =>
  http.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);

// ---- Public + customer onboarding ----
export const getPublicBusiness = (slug) => http.get(`/business/${slug}`).then((r) => r.data);
// Anonymous feedback — deliberately a clean axios call (no auth interceptor) so no
// Authorization header / Loyalty identity is ever attached. Kept separate from Loyalty.
export const submitFeedback = (slug, payload) =>
  axios.post(`${API}/feedback/${slug}`, payload).then((r) => r.data);
// Public digital menu — clean axios call (no auth interceptor), like submitFeedback.
export const getPublicMenu = (slug) => axios.get(`${API}/menu/public/${slug}`).then((r) => r.data);
// ---- Owner feedback overview ----
export const getFeedback = () => http.get("/feedback").then((r) => r.data);
export const markFeedbackRead = (id) => http.patch(`/feedback/${id}/read`).then((r) => r.data);
export const deleteFeedbackMany = (ids) => http.post("/feedback/delete-many", { ids }).then((r) => r.data);
export const deleteAllFeedback = () => http.delete("/feedback").then((r) => r.data);
// ---- Owner menu (module-gated) ----
export const getMenu = () => http.get("/menu").then((r) => r.data);
export const createMenuCategory = (data) => http.post("/menu/categories", data).then((r) => r.data);
export const updateMenuCategory = (id, data) => http.patch(`/menu/categories/${id}`, data).then((r) => r.data);
export const reorderMenuCategories = (ids) => http.post("/menu/categories/reorder", { ids }).then((r) => r.data);
export const deleteMenuCategory = (id) => http.delete(`/menu/categories/${id}`).then((r) => r.data);
export const createMenuItem = (data) => http.post("/menu/items", data).then((r) => r.data);
export const updateMenuItem = (id, data) => http.patch(`/menu/items/${id}`, data).then((r) => r.data);
export const reorderMenuItems = (categoryId, ids) => http.post("/menu/items/reorder", { category_id: categoryId, ids }).then((r) => r.data);
export const deleteMenuItem = (id) => http.delete(`/menu/items/${id}`).then((r) => r.data);
export const downloadMenuStandPdf = (size) =>
  http.get("/menu/stand", { params: { size, base: window.location.origin }, responseType: "blob" }).then((r) => r.data);
export const analyzeMenuImport = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return http.post("/menu/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const applyMenuImport = (data) => http.post("/menu/import/apply", data).then((r) => r.data);
export const feedbackPosterPdfUrl = (slug, format) =>
  `${API}/feedback-poster/${slug}/pdf?format=${format}&url=${encodeURIComponent(`${window.location.origin}/feedback/${slug}`)}`;
export const joinApi = (slug, name) => http.post(`/join/${slug}`, { name }).then((r) => r.data);
export const restoreApi = (recovery_code) =>
  http.post("/customer/restore", { recovery_code }).then((r) => r.data);
export const getMyState = () => http.get("/my/state", { params: { _: Date.now() } }).then((r) => r.data);

// ---- Owner: business + staff ----
export const getMyBusiness = () => http.get("/my/business").then((r) => r.data);
export const getMyContext = () => http.get("/my/context").then((r) => r.data);
export const createBusiness = (payload) => http.post("/my/business", payload).then((r) => r.data);
export const updateBusiness = (payload) => http.put("/my/business", payload).then((r) => r.data);
export const uploadLogo = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return http.post("/my/business/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const getStaff = () => http.get("/my/staff").then((r) => r.data);
export const createStaffApi = (payload) => http.post("/my/staff", payload).then((r) => r.data);
export const deleteStaffApi = (id) => http.delete(`/my/staff/${id}`).then((r) => r.data);
export const resetStaffPassword = (id, password) =>
  http.post(`/my/staff/${id}/reset-password`, { password }).then((r) => r.data);
export const setStaffStatus = (id, active) =>
  http.post(`/my/staff/${id}/status`, { active }).then((r) => r.data);

// ---- Platform admin ----
export const adminBusinesses = () => http.get("/admin/businesses").then((r) => r.data);
export const adminCreateBusiness = (payload) => http.post("/admin/businesses", payload).then((r) => r.data);
export const adminStats = () => http.get("/admin/stats").then((r) => r.data);
export const resetOwnerPassword = (bid, password) =>
  http.post(`/admin/businesses/${bid}/reset-owner-password`, { password }).then((r) => r.data);
export const setBusinessStatus = (bid, active) =>
  http.post(`/admin/businesses/${bid}/status`, { active }).then((r) => r.data);
export const setBusinessModules = (bid, modules) =>
  http.patch(`/admin/businesses/${bid}/modules`, modules).then((r) => r.data);
export const setBusinessWallet = (bid, wallet) =>
  http.patch(`/admin/businesses/${bid}/wallet`, wallet).then((r) => r.data);

// ---- Shop operations (staff/owner) ----
export const getCustomers = () => http.get("/customers").then((r) => r.data);
export const getState = (id) => http.get(`/customers/${id}/state`).then((r) => r.data);
export const addStamp = (id, actionId, opts = {}) =>
  http.post(`/customers/${id}/stamp`, { action_id: actionId, ...opts }).then((r) => r.data);
export const redeemReward = (rewardId) =>
  http.post(`/rewards/${rewardId}/redeem`).then((r) => r.data);
export const resolveQr = (code) => http.post("/staff/resolve-qr", { code }).then((r) => r.data);
export const addGoogleWallet = () => http.post("/wallet/google").then((r) => r.data);
// Returns the raw signed .pkpass as a Blob.
export const addAppleWallet = () =>
  http.post("/wallet/apple", null, { responseType: "blob" }).then((r) => r.data);
export const resolveWallet = (code) => http.post("/staff/resolve-wallet", { code }).then((r) => r.data);
export const getStats = () => http.get("/stats").then((r) => r.data);
export const getAuditLog = (params = {}) => http.get("/audit-log", { params }).then((r) => r.data);
export const resetPilot = (confirm) => http.post("/admin/reset-pilot", { confirm }).then((r) => r.data);
export const createLoyaltyAction = () => http.post("/loyalty-actions").then((r) => r.data);
export const getMyLoyaltyAction = (id) => http.get(`/loyalty-actions/${id}`).then((r) => r.data);
export const getStaffLoyaltyActions = () => http.get("/staff/loyalty-actions").then((r) => r.data);
export const setLoyaltyRequestAccess = (customerId, blocked) => http.patch(`/customers/${customerId}/loyalty-request-access`, { blocked }).then((r) => r.data);

// ---- Physical cards (owner) ----
export const getPhysSummary = () => http.get("/physical-cards/summary").then((r) => r.data);
export const getPhysBatches = () => http.get("/physical-cards/batches").then((r) => r.data);
export const createPhysBatch = (count, print_version = "single") =>
  http.post("/physical-cards/batches", { count, print_version }).then((r) => r.data);
export const getPhysCards = (params = {}) => http.get("/physical-cards", { params }).then((r) => r.data);
export const blockPhysCard = (id) => http.post(`/physical-cards/${id}/block`).then((r) => r.data);
export const archivePhysCard = (id) => http.post(`/physical-cards/${id}/archive`).then((r) => r.data);
export const physBatchPdfUrl = (id, sheet = 1) => `${API}/physical-cards/batches/${id}/pdf?sheet=${sheet}`;
export const downloadPhysBatchPdf = (id, sheet = 1) =>
  http.get(`/physical-cards/batches/${id}/pdf`, { params: { sheet }, responseType: "blob" }).then((r) => r.data);
// ---- Physical cards (staff) ----
export const resolvePhysical = (code) => http.post("/staff/resolve-physical", { code }).then((r) => r.data);
export const activatePhysical = (id, opts = {}) =>
  http.post(`/staff/physical-cards/${id}/activate`, opts).then((r) => r.data);
export const replacePhysCard = (old_card_id, new_secure_token) =>
  http.post("/physical-cards/replace", { old_card_id, new_secure_token }).then((r) => r.data);
